import { useState, useEffect, useMemo, useCallback } from 'react'
import Layout from '../../components/Layout'
import Toast from '../../utils/toast'
import { authService } from '../../services/authService'
import bettingConfigService from '../../services/bettingConfigService'

/**
 * GlobalSettings — owner-only.
 *
 * Buffer = kitne second bet slip live price ko dekhta rehta hai user ke "Place
 * Bet" dabane ke baad, bet actually book hone se pehle.
 *
 * Do level hain:
 *   1. Platform default (platform_configurations.buffer_time) — poore site ka
 *      fallback. Jab niche wali grid me 0 ho to yahi lagta hai.
 *   2. Per sport × market category, aur per casino game — grid me. 0 ka matlab
 *      "set nahi hai", yaani platform default par wapas.
 *
 * Sports feed ke gtype se map hote hain:
 *   Match Odds = 'match', Bookmaker = 'match1', Fancy = baaki sab (fancy,
 *   fancy1, fancy2, meter, khado, oddeven, cricket casino).
 * Casino per game hai — wahi game key jo bet payload me jaati hai, isliye
 * VIP / Virtual / Premium tables bhi cover ho jaate hain.
 */

const TABS = [
  { id: 'sports', label: 'Sports' },
  { id: 'casino', label: 'Casino' },
]

const CATEGORY_LABELS = {
  match_odds: 'Match Odds',
  bookmaker: 'Bookmaker',
  fancy: 'Fancy',
}

// Server ke market `group` → UI ka column. GET jawab na de to yahi fallback.
const GROUP_TO_CATEGORY = {
  Main: 'match_odds',
  Bookmaker: 'bookmaker',
  Fancy: 'fancy',
}

const DEFAULT_MARKET_GROUPS = {
  match_odds: ['matchodds', 'match_other'],
  bookmaker: ['bookmaker', 'match1_other'],
  fancy: ['fancy', 'fancy1', 'fancy2', 'oddeven', 'khado', 'meter', 'badlo', 'cricketcasino', 'other'],
}

/** GET ke `markets` se { match_odds: [...keys], bookmaker: [...], fancy: [...] }. */
function buildMarketGroups(markets) {
  if (!Array.isArray(markets) || markets.length === 0) return DEFAULT_MARKET_GROUPS
  const groups = { match_odds: [], bookmaker: [], fancy: [] }
  for (const m of markets) {
    const category = GROUP_TO_CATEGORY[m?.group] || 'fancy'
    if (m?.key) groups[category].push(m.key)
  }
  // koi bucket khaali reh gaya to uske liye fallback
  for (const key of Object.keys(groups)) {
    if (groups[key].length === 0) groups[key] = DEFAULT_MARKET_GROUPS[key]
  }
  return groups
}

/** Default league ke cells se ek bucket ki value — group ki sabse badi seconds. */
function bucketSeconds(sport, keys) {
  const league = (sport?.leagues || []).find((l) => l.is_default) || (sport?.leagues || [])[0]
  if (!league?.cells) return Number(sport?.buffers?.[keys?.[0]] || 0)
  return keys.reduce((max, k) => Math.max(max, Number(league.cells[k]?.seconds) || 0), 0)
}

const CASINO_CATEGORY_LABELS = {
  our: 'Our Casino',
  vip: 'VIP',
  virtual: 'Virtual',
  premium: 'Premium',
}

function GlobalSettings() {
  // ── platform-wide default ──
  const [bufferTime, setBufferTime] = useState('')
  const [loading, setLoading] = useState(false)
  const [fetching, setFetching] = useState(true)
  const [platformConfig, setPlatformConfig] = useState({})

  // ── per sport / per game grid ──
  const [activeTab, setActiveTab] = useState('sports')
  const [gridLoading, setGridLoading] = useState(true)
  const [sports, setSports] = useState([])
  const [categories, setCategories] = useState(['match_odds', 'bookmaker', 'fancy'])
  // UI ka ek bucket ab server ke KAI market keys par jaata hai. Server 2026-09-15
  // se 'match_odds' ko do markets me tod chuka hai (matchodds + match_other),
  // isliye purana `category: 'match_odds'` bhejne par wo 400 deta hai:
  // "'match_odds' is no longer a single market — use matchodds or match_other".
  // Ye map GET ke `markets` (har ek me `group`) se banta hai, hardcode nahi.
  const [marketGroups, setMarketGroups] = useState(DEFAULT_MARKET_GROUPS)
  const [leagueKey, setLeagueKey] = useState('default')   // server ka DEFAULT_LEAGUE_KEY
  const [maxSeconds, setMaxSeconds] = useState(60)
  const [games, setGames] = useState([])
  const [search, setSearch] = useState('')
  const [savingKey, setSavingKey] = useState(null)
  // Draft edits, keyed "<sportId>:<category>" / "casino:<gameName>". Sirf Save
  // par commit hote hain, taaki aadha type kiya number kabhi POST na ho.
  const [drafts, setDrafts] = useState({})

  // Fetch platform default on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setFetching(true)
        const response = await authService.getOwnerProfile()

        if (response && response.success && response.data) {
          const userData = response.data
          let config = {}

          // Handle platform_configurations (might be string or object)
          if (userData.platform_configurations) {
            try {
              config = typeof userData.platform_configurations === 'string'
                ? JSON.parse(userData.platform_configurations)
                : userData.platform_configurations
            } catch (e) {
              console.error('Failed to parse platform_configurations:', e)
            }
          }

          setPlatformConfig(config)

          if (config.buffer_time) {
            setBufferTime(config.buffer_time)
          }
        }
      } catch (error) {
        console.error('Failed to fetch settings:', error)
        Toast.fire({
          icon: 'error',
          title: 'Failed to load settings'
        })
      } finally {
        setFetching(false)
      }
    }

    fetchSettings()
  }, [])

  const loadGrid = useCallback(async (tab) => {
    setGridLoading(true)
    try {
      if (tab === 'sports') {
        const response = await bettingConfigService.getSportsBuffers()
        if (response.success) {
          setSports(response.sports || [])
          setMarketGroups(buildMarketGroups(response.markets))
          setLeagueKey(response.default_league_key || 'default')
          // Columns d99 ke apne teen hi rehte hain (Match Odds / Bookmaker /
          // Fancy) — server ke 13 market keys inhi ke andar map hote hain.
          if (response.categories) setCategories(response.categories)
          setMaxSeconds(response.max_seconds || 60)
        }
      } else {
        const response = await bettingConfigService.getCasinoBuffers()
        if (response.success) {
          setGames(response.games || [])
          setMaxSeconds(response.max_seconds || 60)
        }
      }
    } catch (error) {
      Toast.fire({ icon: 'error', title: error.response?.data?.error || error.message })
    } finally {
      setGridLoading(false)
    }
  }, [])

  useEffect(() => {
    loadGrid(activeTab)
    setDrafts({})
    setSearch('')
  }, [activeTab, loadGrid])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      const updatedConfig = {
        ...platformConfig,
        buffer_time: bufferTime
      }

      const payload = {
        platform_configurations: updatedConfig
      }

      const response = await authService.updatePlatformConfigurations(payload)

      if (response && response.success) {
        setPlatformConfig(updatedConfig)
        Toast.fire({
          icon: 'success',
          title: 'Settings saved successfully'
        })
      } else {
        throw new Error(response?.message || 'Update failed')
      }
    } catch (error) {
      console.error('Error saving settings:', error)
      Toast.fire({
        icon: 'error',
        title: 'Failed to save settings: ' + (error.response?.data?.message || error.message)
      })
    } finally {
      setLoading(false)
    }
  }

  const validSeconds = (raw) => {
    const value = Number(raw)
    if (!Number.isFinite(value) || value < 0 || value > maxSeconds) {
      Toast.fire({ icon: 'error', title: `Buffer must be between 0 and ${maxSeconds} seconds` })
      return null
    }
    return value
  }

  const clearDraft = (key) => setDrafts((prev) => {
    const next = { ...prev }
    delete next[key]
    return next
  })

  const saveSport = async (sport, category) => {
    const key = `${sport.sport_id}:${category}`
    const value = validSeconds(drafts[key])
    if (value === null) return

    setSavingKey(key)
    try {
      // Ek bucket = server ke kai market keys, isliye sab ek bulk call me jaate
      // hain. Payload `market_key` hai, purana `category` nahi.
      const keys = marketGroups[category] || [category]
      const items = keys.map((marketKey) => ({
        sport_id: sport.sport_id,
        league_key: leagueKey,
        market_key: marketKey,
        buffer_seconds: value,
      }))
      const response = items.length === 1
        ? await bettingConfigService.saveSportsBuffer(items[0])
        : await bettingConfigService.saveSportsBufferBulk(items)
      if (response.success) {
        // Default league ke cells local hi update kar dete hain, taaki poora
        // grid dobara fetch na karna pade.
        setSports((prev) => prev.map((s) => {
          if (s.sport_id !== sport.sport_id) return s
          const leagues = (s.leagues || []).map((l) => (
            l.is_default
              ? {
                ...l,
                cells: keys.reduce((cells, k) => ({
                  ...cells,
                  [k]: { ...(cells[k] || {}), state: 'set', seconds: value },
                }), { ...l.cells }),
              }
              : l
          ))
          return { ...s, leagues, buffers: { ...s.buffers, [category]: value } }
        }))
        clearDraft(key)
        Toast.fire({
          icon: 'success',
          title: value > 0
            ? `${sport.name} — ${CATEGORY_LABELS[category] || category} buffer set to ${value}s`
            : `${sport.name} — ${CATEGORY_LABELS[category] || category} buffer cleared`,
        })
      }
    } catch (error) {
      Toast.fire({ icon: 'error', title: error.response?.data?.error || error.message })
    } finally {
      setSavingKey(null)
    }
  }

  const saveGame = async (game) => {
    const key = `casino:${game.game_name}`
    const value = validSeconds(drafts[key])
    if (value === null) return

    setSavingKey(key)
    try {
      const response = await bettingConfigService.saveCasinoBuffer({
        game_name: game.game_name,
        buffer_seconds: value,
      })
      if (response.success) {
        setGames((prev) => prev.map((g) => (
          g.game_name === game.game_name ? { ...g, buffer_seconds: value } : g
        )))
        clearDraft(key)
        Toast.fire({
          icon: 'success',
          title: value > 0 ? `${game.name} buffer set to ${value}s` : `${game.name} buffer cleared`,
        })
      }
    } catch (error) {
      Toast.fire({ icon: 'error', title: error.response?.data?.error || error.message })
    } finally {
      setSavingKey(null)
    }
  }

  const visibleGames = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return games
    return games.filter(
      (g) => g.name.toLowerCase().includes(term) || g.game_name.includes(term)
    )
  }, [games, search])

  const visibleSports = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return sports
    return sports.filter((s) => s.name.toLowerCase().includes(term))
  }, [sports, search])

  const cellValue = (key, saved) => (drafts[key] !== undefined ? drafts[key] : saved)
  const isDirty = (key, saved) => drafts[key] !== undefined && Number(drafts[key]) !== Number(saved)

  return (
    <Layout title="Global Settings - Diamond Admin">
      <div className="row">
        <div className="col-12">
          <div className="page-title-box d-flex align-items-center justify-content-between">
            <h4 className="mb-0 font-size-18">Global Settings</h4>
          </div>
        </div>
      </div>

      {/* Platform-wide default — grid me 0 hone par yahi lagta hai */}
      <div className="row">
        <div className="col-lg-6">
          <div className="card">
            <div className="card-body">
              <h5 className="card-title mb-3">Platform Default</h5>

              {fetching ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className="row mb-4">
                    <label htmlFor="bufferTime" className="col-sm-3 col-form-label">
                      Buffer Time (sec)
                    </label>
                    <div className="col-sm-9">
                      <input
                        type="number"
                        className="form-control"
                        id="bufferTime"
                        placeholder="Enter buffer time in seconds"
                        value={bufferTime}
                        onChange={(e) => setBufferTime(e.target.value)}
                        min="0"
                        required
                      />
                      <small className="text-muted">
                        Site-wide fallback. Kisi sport ya game ka apna buffer 0 ho to yahi lagta hai.
                      </small>
                    </div>
                  </div>

                  <div className="row justify-content-end">
                    <div className="col-sm-9">
                      <button
                        type="submit"
                        className="btn btn-primary w-md"
                        disabled={loading}
                      >
                        {loading ? 'Saving...' : 'Save Settings'}
                      </button>
                    </div>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Per sport × category / per casino game */}
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <h5 className="card-title mb-3">Buffer Time</h5>

              <ul className="nav nav-tabs mb-3">
                {TABS.map((tab) => (
                  <li className="nav-item" key={tab.id}>
                    <button
                      type="button"
                      className={`nav-link ${activeTab === tab.id ? 'active' : ''}`}
                      onClick={() => setActiveTab(tab.id)}
                    >
                      {tab.label}
                    </button>
                  </li>
                ))}
              </ul>

              <p className="text-muted mb-3" style={{ fontSize: '12px' }}>
                Seconds the bet slip tracks the live price before booking the bet.
                Leave 0 to keep the site default. Maximum {maxSeconds}s.
              </p>

              <div className="mb-3" style={{ maxWidth: '320px' }}>
                <input
                  type="search"
                  className="form-control form-control-sm"
                  placeholder={activeTab === 'sports' ? 'Search sport…' : 'Search game…'}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              {gridLoading ? (
                <div className="text-center p-4">Loading...</div>
              ) : activeTab === 'sports' ? (
                <div className="table-responsive">
                  <table className="table table-sm table-bordered mb-0">
                    <thead className="table-light">
                      <tr>
                        <th style={{ minWidth: '180px' }}>Sport</th>
                        {categories.map((category) => (
                          <th key={category} className="text-center" style={{ minWidth: '200px' }}>
                            {CATEGORY_LABELS[category] || category}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {visibleSports.length === 0 && (
                        <tr>
                          <td colSpan={categories.length + 1} className="text-center">
                            no records found
                          </td>
                        </tr>
                      )}
                      {visibleSports.map((sport) => (
                        <tr key={sport.sport_id}>
                          <td>
                            {sport.name}{' '}
                            <small className="text-muted">#{sport.sport_id}</small>
                            {sport.enabled ? (
                              <span className="badge bg-success ms-1">live</span>
                            ) : null}
                          </td>
                          {categories.map((category) => {
                            const key = `${sport.sport_id}:${category}`
                            const saved = bucketSeconds(sport, marketGroups[category] || [category])
                            return (
                              <td key={category}>
                                <div className="d-flex align-items-center">
                                  <input
                                    type="number"
                                    min="0"
                                    max={maxSeconds}
                                    step="0.5"
                                    className="form-control form-control-sm me-2"
                                    style={{ maxWidth: '90px' }}
                                    value={cellValue(key, saved)}
                                    onChange={(e) => setDrafts((prev) => ({ ...prev, [key]: e.target.value }))}
                                  />
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-primary"
                                    disabled={!isDirty(key, saved) || savingKey === key}
                                    onClick={() => saveSport(sport, category)}
                                  >
                                    {savingKey === key ? '…' : 'Save'}
                                  </button>
                                </div>
                              </td>
                            )
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm table-bordered mb-0">
                    <thead className="table-light">
                      <tr>
                        <th style={{ minWidth: '220px' }}>Game</th>
                        <th style={{ minWidth: '120px' }}>Category</th>
                        <th style={{ minWidth: '220px' }}>Buffer (seconds)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleGames.length === 0 && (
                        <tr><td colSpan={3} className="text-center">no records found</td></tr>
                      )}
                      {visibleGames.map((game) => {
                        const key = `casino:${game.game_name}`
                        const saved = game.buffer_seconds || 0
                        return (
                          <tr key={game.game_name}>
                            <td>
                              {game.name} <code className="ms-1">{game.game_name}</code>
                            </td>
                            <td>{CASINO_CATEGORY_LABELS[game.category] || game.category}</td>
                            <td>
                              <div className="d-flex align-items-center">
                                <input
                                  type="number"
                                  min="0"
                                  max={maxSeconds}
                                  step="0.5"
                                  className="form-control form-control-sm me-2"
                                  style={{ maxWidth: '90px' }}
                                  value={cellValue(key, saved)}
                                  onChange={(e) => setDrafts((prev) => ({ ...prev, [key]: e.target.value }))}
                                />
                                <button
                                  type="button"
                                  className="btn btn-sm btn-primary"
                                  disabled={!isDirty(key, saved) || savingKey === key}
                                  onClick={() => saveGame(game)}
                                >
                                  {savingKey === key ? '…' : 'Save'}
                                </button>
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  )
}

export default GlobalSettings
